import React, { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { Table, Skeleton } from 'antd'
import moment from 'moment'
import { FaCarAlt, FaCamera, FaUser, FaMap, FaEye, FaBus, FaMapMarkerAlt, FaLink, FaChartBar, FaMapMarkedAlt, FaRoute, FaRoad, FaTachometerAlt, FaBullseye, FaUsers } from 'react-icons/fa'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import { Charts, DashboardMap, LineVehiclesDrawer } from '../../Components'
import { GET } from '../../utils/apis'
import { secureApi } from '../../Config/api'
import { fetchAllOrganization, fetchDevices, fetchUsers, fetchVehicles, verifyToken, convertTitle } from '../../utils/helpers'
import { setUserData } from '../../Redux/actions/userAction'
import { setDevicesData, setTotalDevices, setDevicePercentage } from '../../Redux/actions/deviceAction'
import { setTotalVehicles, setVechiclesData, setVehiclePercentage } from '../../Redux/actions/vehiclesAction'
import { setUserPercentage } from '../../Redux/actions/userAction'
import { allPaths, translations } from '../../utils/constants'

const Home = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const authUser = useSelector(state => state?.authReducer?.user)
    const usersFromRedux = useSelector((state) => state?.userReducer?.users)
    const user = useSelector(state => state?.authReducer?.user)
    const organizationDetails = useSelector(state => state?.authReducer?.organization)
    const totalVehicles = useSelector((state) => state.vehiclesReducer?.totalVehicle)
    const totalDevices = useSelector((state) => state.deviceReducer?.totalDevices)
    const language = useSelector((state) => state.languageReducer?.language)
    const vehicles = useSelector(state => state.vehiclesReducer?.vehicles)
    const monthStats = useSelector(state => state?.monthlyStatsReducers?.monthStats)
    const [totalUsers, setTotalUser] = useState(usersFromRedux?.length)
    const [loading, setLoading] = useState(false)
    const [users, setUsers] = useState([])
    const [devices, setDevices] = useState([])
    const [gtfsStats, setGtfsStats] = useState(null)
    const [lineVehicleCounts, setLineVehicleCounts] = useState([])
    // Today's fleet route history (trips, km, adherence) — null while loading,
    // { error: true } when the call failed.
    const [routeStats, setRouteStats] = useState(null)
    // Line whose vehicles are open in the right-side drawer.
    const [openLine, setOpenLine] = useState(null)
    const [vehiclesLoading, setVehiclesLoading] = useState(true)
    const [devicesLoading, setDevicesLoading] = useState(true)
    const organizationId = organizationDetails?.id
    const isAdmin = ['admin', 'superAdmin'].includes(user?.role)

    // Re-compute the dashboard date whenever the language changes so the day
    // and month names localize immediately without waiting for a page refresh.
    const formattedDate = useMemo(
        () => moment().locale(language || 'en').format('dddd, DD MMMM YYYY'),
        [language]
    )

    useEffect(() => {
        verifyToken(history, dispatch)
        fetchUsers({ setLoading, setUsers, dispatch, setUserData, authUser, setUserPercentage, organizationId })
        // Wrapped setters flip the skeletons off as soon as data lands (both
        // the success and the empty-failure paths go through them).
        fetchDevices({
            setDevicesData,
            setTotalDevices,
            dispatch,
            setDevices: (d) => {
                setDevices(d)
                setDevicesLoading(false)
            },
            user,
            organizationId,
            setDevicePercentage,
        })
        fetchVehicles({
            setTotalVehicles,
            setVechiclesData: (d) => {
                setVehiclesLoading(false)
                return setVechiclesData(d)
            },
            dispatch,
            user,
            organizationId,
            setVehiclePercentage,
        })
        fetchAllOrganization({ dispatch })
        // GTFS APIs reject non-admin roles, so only admins fetch this.
        if (isAdmin) fetchGtfsStats()
        fetchRouteStats()
    }, [organizationId, user])

    // Today's routes: the same fleet summary the Route History page shows,
    // scoped to the user's organisation (whole fleet for a superAdmin).
    // The whole-fleet build can be slow right after a server restart; a
    // failed/timed-out call is retried a few times (with a pause) before the
    // block gives up and shows a retry button.
    const ROUTE_STATS_RETRY_DELAYS_MS = [4000, 10000, 20000]
    // The server answers at once with `building: true` while it computes the
    // fleet's day in the background: keep the skeleton and poll (up to
    // ~2 minutes) instead of showing "no routes" or waiting on the request.
    const ROUTE_STATS_BUILD_POLL_MS = 4000
    const ROUTE_STATS_BUILD_MAX_POLLS = 30
    const fetchRouteStats = async (attempt = 0, buildPoll = 0) => {
        if (attempt === 0 && buildPoll === 0) setRouteStats(null)
        try {
            const response = await secureApi?.get(GET?.GET_FLEET_ROUTE_HISTORY, {
                params: {
                    date: moment().format('YYYY-MM-DD'),
                    user_id: user?.id,
                    organization_id: organizationId,
                },
            })
            if (response?.success && response?.data?.building) {
                if (buildPoll < ROUTE_STATS_BUILD_MAX_POLLS) {
                    setTimeout(() => fetchRouteStats(attempt, buildPoll + 1), ROUTE_STATS_BUILD_POLL_MS)
                } else {
                    setRouteStats({ error: true })
                }
                return
            }
            if (response?.success) {
                setRouteStats(response?.data || { stats: null })
                return
            }
            throw new Error(response?.message || 'route stats failed')
        } catch (e) {
            if (attempt < ROUTE_STATS_RETRY_DELAYS_MS.length) {
                setTimeout(() => fetchRouteStats(attempt + 1), ROUTE_STATS_RETRY_DELAYS_MS[attempt])
            } else {
                setRouteStats({ error: true })
            }
        }
    }

    useEffect(() => {
        setTotalUser(usersFromRedux?.length)
    }, [usersFromRedux])

    // GTFS overview for the dashboard: feeds come from one call, the lines and
    // per-line vehicle totals from the same per-feed aggregates the GTFS pages use.
    const fetchGtfsStats = async () => {
        try {
            const response = await secureApi?.get(GET?.GET_GTFS_FEEDS)
            if (!response?.success) return
            const rows = response?.data || []
            // Line count ships with the feeds call now — no per-feed lines
            // aggregate (a stop_times scan each) just to count routes.
            const lines = rows.reduce((sum, f) => sum + (f?.routes || 0), 0)
            const lineCounts = {}
            await Promise.all(
                rows.map(async (feed) => {
                    try {
                        const vehRes = await secureApi?.get(GET?.GET_GTFS_VEHICLES, {
                            params: { feed_source: feed?.feed_source },
                        })
                        if (vehRes?.success) {
                            ;(vehRes?.data || []).forEach((v) => {
                                if (v?.line) lineCounts[v.line] = (lineCounts[v.line] || 0) + 1
                            })
                        }
                    } catch (e) { }
                })
            )
            setGtfsStats({
                feeds: rows.length,
                lines,
                stops: rows.reduce((sum, f) => sum + (f?.stops || 0), 0),
                attachedVehicles: rows.reduce((sum, f) => sum + (f?.attached_vehicles || 0), 0),
            })
            setLineVehicleCounts(
                Object.entries(lineCounts)
                    .map(([line, count]) => ({ line, count }))
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 8)
            )
        } catch (e) { }
    }

    const activeVehicles = vehicles?.filter((v) => v?.is_active)?.length || 0
    const inactiveVehicles = (vehicles?.length || 0) - activeVehicles
    const onlineDevices = devices?.filter((d) => d?.is_online)?.length || 0
    const offlineDevices = (devices?.length || 0) - onlineDevices

    // New registrations this calendar month, from the same monthly stats the
    // Statistics chart plots — shown as the "+N this month" chip on KPI cards.
    const newThisMonth = (rows) => {
        const key = moment().format('YYYY-MM')
        return parseInt(rows?.find((r) => r?.month === key)?.count || 0, 10)
    }

    const greeting = () => {
        const hour = moment().hour()
        if (hour < 12) return translations[language]?.goodMorning
        if (hour < 18) return translations[language]?.goodAfternoon
        return translations[language]?.goodEvening
    }

    // progress: 0-100 share shown as a thin bar under the card (e.g. active
    // vehicles out of all vehicles); null hides the bar.
    const kpiCard = (label, count, subtitle, color, Icon, onClick, chip, loadingCard, progress = null) => (
        <div
            className='dash-kpi-card'
            key={label}
            onClick={loadingCard ? undefined : onClick}
            style={{ '--kpi-color': color }}
        >
            {loadingCard ? (
                <Skeleton active avatar paragraph={{ rows: 1 }} />
            ) : (
                <>
                    <div className='dash-kpi-main'>
                        <div className='dash-kpi-icon' style={{ backgroundColor: `${color}1A`, color }}>
                            <Icon size={22} />
                        </div>
                        <div className='dash-kpi-info'>
                            <p className='dash-kpi-count'>
                                {count ?? '-'}
                                {chip > 0 && (
                                    <span className='dash-kpi-chip'>+{chip} {translations[language]?.thisMonth}</span>
                                )}
                            </p>
                            <p className='dash-kpi-label'>{label}</p>
                            {subtitle ? <p className='dash-kpi-sub'>{subtitle}</p> : null}
                        </div>
                    </div>
                    {progress !== null && Number.isFinite(progress) && (
                        <div className='dash-kpi-progress' title={`${Math.round(progress)}%`}>
                            <span style={{ width: `${Math.max(0, Math.min(100, progress))}%`, background: color }} />
                        </div>
                    )}
                </>
            )}
        </div>
    )

    const share = (part, total) => (total > 0 ? (part / total) * 100 : 0)

    const titleRow = (title, viewAllPath, count = null) => (
        <div className='dash-card-title-row'>
            <h1 className='dash-card-title'>
                {title}
                {count !== null && count !== undefined && (
                    <span className='dash-count-chip'>{count}</span>
                )}
            </h1>
            {viewAllPath && (
                <span className='dash-view-all' onClick={() => history.push(viewAllPath)}>
                    {translations[language]?.viewAll} →
                </span>
            )}
        </div>
    )

    const statusBadge = (ok, okText, koText) => (
        <span className={`dash-status-pill ${ok ? 'dash-status-ok' : 'dash-status-ko'}`}>
            <span className='dash-status-dot' />
            {ok ? okText : koText}
        </span>
    )

    // Round icon button used by the "details" column.
    const detailsButton = (onClick) => (
        <button
            type='button'
            className='dash-icon-btn'
            onClick={(e) => {
                e.stopPropagation()
                onClick()
            }}
            aria-label='details'
        >
            <FaEye size={13} />
        </button>
    )

    const initials = (name) =>
        String(name || '')
            .trim()
            .split(/\s+/)
            .slice(0, 2)
            .map((w) => w[0])
            .join('')
            .toUpperCase() || '-'

    const vehicleColumns = [
        {
            title: '#',
            key: 'key',
            render: (text, record, index) => <div>{index + 1}</div>,
        },
        {
            title: translations[language]?.vehicleName,
            dataIndex: 'vehicle_name',
            key: 'vehicleName',
            render: (text) => (
                <span className='dash-name-cell'>
                    <span className='dash-avatar'>{initials(text)}</span>
                    <span className='dash-name-text'>{convertTitle(text)}</span>
                </span>
            ),
        },
        {
            title: translations[language]?.totalDevices,
            dataIndex: 'device_count',
            key: 'Total Devices',
            render: (value) => (
                <span className={`dash-count-chip${!value ? ' dash-count-chip-zero' : ''}`}>{value ?? 0}</span>
            ),
        },
        {
            title: translations[language]?.status,
            key: 'status',
            render: (_, record) =>
                statusBadge(record?.is_active, translations[language]?.active, translations[language]?.inactive),
        },
        {
            title: translations[language]?.details,
            key: 'details',
            width: 90,
            render: () => detailsButton(() => history?.push(allPaths?.VEHICLES)),
        },
    ]

    const deviceColumns = [
        {
            title: '#',
            key: 'key',
            render: (text, record, index) => <div>{index + 1}</div>,
        },
        {
            title: translations?.[language]?.serialNumber,
            dataIndex: 'serial_number',
            key: 'serialNumber',
            render: (text) => (
                <span className='dash-serial'>{!text || text === 'NULL' ? '-' : text}</span>
            ),
        },
        {
            title: translations[language]?.status,
            key: 'status',
            render: (_, record) =>
                statusBadge(record?.is_online, translations[language]?.online, translations[language]?.offline),
        },
        {
            title: translations[language]?.details,
            key: 'details',
            width: 90,
            render: () => detailsButton(() => history?.push(allPaths?.DEVICES)),
        },
    ]

    const donutBlock = (title, okLabel, okValue, okColor, koLabel, koValue) => {
        const data = [
            { name: okLabel, value: okValue },
            { name: koLabel, value: koValue },
        ]
        const total = okValue + koValue
        const pct = (v) => (total > 0 ? ` (${Math.round((v / total) * 100)}%)` : '')
        return (
            <div className='dash-donut-item'>
                <h3>{title}</h3>
                <div className='dash-donut-chart'>
                    <ResponsiveContainer width='100%' height={150}>
                        <PieChart>
                            <Pie
                                data={data}
                                dataKey='value'
                                innerRadius={45}
                                outerRadius={65}
                                startAngle={90}
                                endAngle={-270}
                                stroke='none'
                            >
                                <Cell fill={okColor} />
                                <Cell fill='#E5E7EB' />
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className='dash-donut-center'>
                        <span className='dash-donut-center-count'>{total}</span>
                        <span className='dash-donut-center-label'>{translations[language]?.total}</span>
                    </div>
                </div>
                <div className='dash-legend'>
                    <span>
                        <span className='dash-legend-dot' style={{ background: okColor }} />
                        {okLabel}: {okValue}{pct(okValue)}
                    </span>
                    <span>
                        <span className='dash-legend-dot' style={{ background: '#E5E7EB' }} />
                        {koLabel}: {koValue}{pct(koValue)}
                    </span>
                </div>
            </div>
        )
    }

    return (
        <div className='main-container'>
            <div className='main-inner-container'>
                <div className='dash-header-row'>
                    <div className='dash-header'>
                        <h1 className='main-heading'>{translations[language]?.dashboard}</h1>
                        <p className='dash-header-sub'>
                            <span>{greeting()}{user?.username ? `, ${user.username}` : ''}</span>
                            <span className='dash-date-pill'>{formattedDate}</span>
                        </p>
                    </div>
                    <div className='dash-quick-actions'>
                        <button type='button' className='dash-quick-btn' onClick={() => history.push(allPaths?.MAP)}>
                            <FaMapMarkedAlt size={14} />
                            {translations[language]?.map}
                        </button>
                        <button type='button' className='dash-quick-btn' onClick={() => history.push(allPaths?.REPORTS)}>
                            <FaChartBar size={14} />
                            {translations[language]?.reports}
                        </button>
                    </div>
                </div>
            </div>
            <div className='insider-container-dashboard'>
                <div className='dashboard-top-flex-box'>
                    <div className='dashboard-top-flex-box-inner'>
                        {kpiCard(
                            translations[language]?.vehicles,
                            totalVehicles,
                            `${activeVehicles} ${translations[language]?.active}`,
                            '#40AFFF',
                            FaCarAlt,
                            () => history.push(allPaths?.VEHICLES),
                            newThisMonth(monthStats?.vehicles),
                            vehiclesLoading,
                            share(activeVehicles, vehicles?.length || 0)
                        )}
                        {kpiCard(
                            translations[language]?.devices,
                            totalDevices,
                            `${onlineDevices} ${translations[language]?.online}`,
                            '#00E096',
                            FaCamera,
                            () => history.push(allPaths?.DEVICES),
                            newThisMonth(monthStats?.devices),
                            devicesLoading,
                            share(onlineDevices, devices?.length || 0)
                        )}
                        {user?.role === 'user'
                            ? null
                            : kpiCard(
                                translations[language]?.users,
                                totalUsers,
                                null,
                                '#1976D2',
                                FaUser,
                                () => history.push(allPaths?.USERS),
                                0,
                                loading
                            )}
                        {isAdmin &&
                            kpiCard(
                                translations[language]?.gtfsFeeds,
                                gtfsStats?.feeds,
                                gtfsStats ? `${gtfsStats?.lines} ${translations[language]?.routes}` : null,
                                '#ED2328',
                                FaMap,
                                () => history.push(allPaths?.GTFS),
                                0,
                                !gtfsStats,
                                share(gtfsStats?.attachedVehicles || 0, vehicles?.length || 0)
                            )}
                    </div>
                </div>

                <div className='dashboard-big-box-flex dash-routes-row'>
                    <div className='Vechicles-div dash-routes-card'>
                        {titleRow(translations[language]?.routesToday, allPaths?.ROUTE_HISTORY)}
                        {routeStats === null ? (
                            <div style={{ padding: '10px 5px' }}>
                                <Skeleton active paragraph={{ rows: 3 }} />
                            </div>
                        ) : routeStats?.error ? (
                            <div className='dash-empty'>
                                <span className='dash-empty-icon'><FaRoute size={26} /></span>
                                <p className='dash-empty-title'>{translations[language]?.routeStatsLoadFailed}</p>
                                <p className='dash-empty-text'>{translations[language]?.routeStatsLoadFailedHint}</p>
                                <button className='add-button' onClick={() => fetchRouteStats()}>
                                    {translations[language]?.retryTxt}
                                </button>
                            </div>
                        ) : !(routeStats?.stats?.trips > 0) ? (
                            <div className='dash-empty'>
                                <span className='dash-empty-icon'><FaRoute size={26} /></span>
                                <p className='dash-empty-title'>{translations[language]?.noTripsForDay}</p>
                                <p className='dash-empty-text'>{translations[language]?.noTripsHint}</p>
                                <button className='add-button' onClick={() => history.push(allPaths?.ROUTE_HISTORY)}>
                                    {translations[language]?.viewRouteHistory}
                                </button>
                            </div>
                        ) : (() => {
                            const s = routeStats.stats
                            const lines = (routeStats.lines || []).slice(0, 5)
                            const maxLine = Math.max(...lines.map((l) => l.trips || 0), 1)
                            const scheduled = (s.normal || 0) + (s.abnormal || 0)
                            const segPct = (n) => (s.trips ? (n / s.trips) * 100 : 0)
                            return (
                                <>
                                    <div className='dash-gtfs-tiles dash-route-tiles'>
                                        <div className='dash-gtfs-tile' onClick={() => history.push(allPaths?.ROUTE_HISTORY)}>
                                            <span className='dash-gtfs-icon' style={{ background: '#40AFFF' }}><FaBus size={14} color='white' /></span>
                                            <div>
                                                <p className='dash-gtfs-count'>
                                                    {s.vehicles_with_trips}
                                                    <span className='dash-route-of'>/ {s.vehicles_total}</span>
                                                </p>
                                                <p className='dash-gtfs-label'>{translations[language]?.vehiclesWithTrips}</p>
                                                <p className='dash-route-sub'>{s.vehicles_total} {translations[language]?.vehiclesWithRouter}</p>
                                            </div>
                                        </div>
                                        <div className='dash-gtfs-tile' onClick={() => history.push(allPaths?.ROUTE_HISTORY)}>
                                            <span className='dash-gtfs-icon' style={{ background: '#ED2328' }}><FaRoute size={14} color='white' /></span>
                                            <div>
                                                <p className='dash-gtfs-count'>{s.trips}</p>
                                                <p className='dash-gtfs-label'>{translations[language]?.totalTrips}</p>
                                                <p className='dash-route-sub'>
                                                    <span className='dash-route-ok'>{s.normal} {translations[language]?.normal?.toLowerCase()}</span>
                                                    {s.abnormal > 0 && <span className='dash-route-ko'> · {s.abnormal} {translations[language]?.abnormal?.toLowerCase()}</span>}
                                                </p>
                                            </div>
                                        </div>
                                        <div className='dash-gtfs-tile' onClick={() => history.push(allPaths?.ROUTE_HISTORY)}>
                                            <span className='dash-gtfs-icon' style={{ background: '#884DFF' }}><FaRoad size={14} color='white' /></span>
                                            <div>
                                                <p className='dash-gtfs-count'>{s.km_traveled} <span className='dash-route-unit'>km</span></p>
                                                <p className='dash-gtfs-label'>{translations[language]?.kmTraveled}</p>
                                                {s.scheduled_km > 0 && (
                                                    <p className='dash-route-sub'>{translations[language]?.scheduledDistance}: {s.scheduled_km} km</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className='dash-gtfs-tile' onClick={() => history.push(allPaths?.ROUTE_HISTORY)}>
                                            <span className='dash-gtfs-icon' style={{ background: '#F59E0B' }}><FaTachometerAlt size={14} color='white' /></span>
                                            <div>
                                                <p className='dash-gtfs-count'>
                                                    {s.avg_speed_kmh ?? '-'} <span className='dash-route-unit'>km/h</span>
                                                </p>
                                                <p className='dash-gtfs-label'>{translations[language]?.avgSpeed}</p>
                                                {s.max_speed_kmh != null && (
                                                    <p className='dash-route-sub'>{translations[language]?.maxSpeed}: {s.max_speed_kmh} km/h</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className='dash-gtfs-tile' onClick={() => history.push(allPaths?.ROUTE_HISTORY)}>
                                            <span className='dash-gtfs-icon' style={{ background: '#00A86B' }}><FaBullseye size={14} color='white' /></span>
                                            <div>
                                                <p className='dash-gtfs-count'>
                                                    {s.avg_adherence != null ? `${Math.round(s.avg_adherence * 100)}%` : '-'}
                                                </p>
                                                <p className='dash-gtfs-label'>{translations[language]?.avgAdherence}</p>
                                                {s.on_schedule_pct != null && (
                                                    <p className='dash-route-sub'>{s.on_schedule_pct}% {translations[language]?.onSchedule?.toLowerCase()}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className='dash-gtfs-tile' onClick={() => history.push(allPaths?.ROUTE_HISTORY)}>
                                            <span className='dash-gtfs-icon' style={{ background: '#9B59B6' }}><FaUsers size={14} color='white' /></span>
                                            <div>
                                                <p className='dash-gtfs-count'>{s.passengers ?? 0}</p>
                                                <p className='dash-gtfs-label'>{translations[language]?.totalPassengers}</p>
                                                {s.trips > 0 && (
                                                    <p className='dash-route-sub'>
                                                        {Math.round((s.passengers ?? 0) / s.trips)} {translations[language]?.perTrip}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className='dash-route-split'>
                                        <div className='dash-route-status'>
                                            <div className='dash-lines-head'>
                                                <span className='dash-sub-heading'>{translations[language]?.statusBreakdown}</span>
                                                <span className='dash-lines-meta'>{s.trips} {translations[language]?.trips}</span>
                                            </div>
                                            <div className='dash-route-bar'>
                                                {s.normal > 0 && <span style={{ width: `${segPct(s.normal)}%`, background: '#52c41a' }} />}
                                                {s.abnormal > 0 && <span style={{ width: `${segPct(s.abnormal)}%`, background: '#ff4d4f' }} />}
                                                {s.no_schedule > 0 && <span style={{ width: `${segPct(s.no_schedule)}%`, background: '#bfbfbf' }} />}
                                            </div>
                                            <div className='dash-legend dash-route-legend'>
                                                <span><span className='dash-legend-dot' style={{ background: '#52c41a' }} />{translations[language]?.normal}: {s.normal}</span>
                                                <span><span className='dash-legend-dot' style={{ background: '#ff4d4f' }} />{translations[language]?.abnormal}: {s.abnormal}</span>
                                                <span><span className='dash-legend-dot' style={{ background: '#bfbfbf' }} />{translations[language]?.noSchedule}: {s.no_schedule}</span>
                                            </div>
                                            {scheduled > 0 && (
                                                <p className='dash-route-note'>
                                                    {s.on_schedule_pct}% {translations[language]?.onSchedule?.toLowerCase()} · {s.vehicles_with_schedule} {translations[language]?.attachedVehicles?.toLowerCase()}
                                                </p>
                                            )}
                                        </div>
                                        {lines.length > 0 && (
                                            <div className='dash-lines dash-route-lines'>
                                                <div className='dash-lines-head'>
                                                    <span className='dash-sub-heading'>{translations[language]?.tripsPerLine}</span>
                                                    <span className='dash-lines-meta'>{(routeStats.lines || []).length} {translations[language]?.routes}</span>
                                                </div>
                                                {lines.map((l) => (
                                                    <div className='dash-line-row dash-line-row-click' key={l.line} onClick={() => setOpenLine(l)} title={translations[language]?.lineVehicles}>
                                                        <span className='dash-line-badge'>{l.line}</span>
                                                        <div className='dash-line-track'>
                                                            <span style={{ width: `${(l.trips / maxLine) * 100}%` }} />
                                                        </div>
                                                        <span className='dash-line-count'>
                                                            {l.trips}
                                                            <span className='dash-line-vehicles'><FaBus size={10} /> {l.vehicles_count ?? 0}</span>
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )
                        })()}
                    </div>
                </div>

                <LineVehiclesDrawer line={openLine} open={!!openLine} onClose={() => setOpenLine(null)} language={language} />

                <div className='dashboard-big-box-flex'>
                    <div className='dashboard-charts'>
                        <div className='dashoabd-inner-charts'>
                            {titleRow(translations[language]?.statics)}
                            <Charts language={language} />
                        </div>
                    </div>
                    <div className='dash-donut'>
                        {titleRow(translations[language]?.fleetStatus)}
                        {vehiclesLoading || devicesLoading ? (
                            <div style={{ padding: '10px 16px' }}>
                                <Skeleton active paragraph={{ rows: 4 }} />
                            </div>
                        ) : (
                            <div className='dash-donut-wrap'>
                                {donutBlock(
                                    translations[language]?.vehicles,
                                    translations[language]?.active,
                                    activeVehicles,
                                    '#00E096',
                                    translations[language]?.inactive,
                                    inactiveVehicles
                                )}
                                {donutBlock(
                                    translations[language]?.devices,
                                    translations[language]?.online,
                                    onlineDevices,
                                    '#40AFFF',
                                    translations[language]?.offline,
                                    offlineDevices
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className='dashboard-big-box-flex'>
                    <div className={isAdmin ? 'dashboard-map' : 'dashboard-map dashboard-map-full'}>
                        <div className='dash-map-title'>
                            <h1 className='dash-card-title'>{translations[language]?.liveFleetMap}</h1>
                            <div className='dash-map-actions'>
                                <span className='dash-live'>
                                    <span className='dash-live-dot' />
                                    {translations[language]?.live}
                                </span>
                                <span className='dash-view-all' onClick={() => history.push(allPaths?.MAP)}>
                                    {translations[language]?.viewAll} →
                                </span>
                            </div>
                        </div>
                        <div className='dash-map-body'>
                            <DashboardMap language={language} />
                        </div>
                    </div>
                    {isAdmin && (
                        <div className='Vechicles-div'>
                            {titleRow(translations[language]?.gtfs, allPaths?.GTFS)}
                            {!gtfsStats ? (
                                <div style={{ padding: '10px 5px' }}>
                                    <Skeleton active paragraph={{ rows: 5 }} />
                                </div>
                            ) : gtfsStats?.feeds === 0 ? (
                                <div className='dash-empty'>
                                    <span className='dash-empty-icon'><FaMap size={28} /></span>
                                    <p className='dash-empty-title'>{translations[language]?.noGtfsFound}</p>
                                    <p className='dash-empty-text'>{translations[language]?.addGtfsToSee}</p>
                                    <button className='add-button' onClick={() => history.push(allPaths?.GTFS)}>
                                        {translations[language]?.addGtfs}
                                    </button>
                                </div>
                            ) : (
                                <>
                            <div className='dash-gtfs-tiles'>
                                <div className='dash-gtfs-tile'>
                                    <span className='dash-gtfs-icon' style={{ background: '#ED2328' }}><FaMap size={14} color='white' /></span>
                                    <div>
                                        <p className='dash-gtfs-count'>{gtfsStats?.feeds}</p>
                                        <p className='dash-gtfs-label'>{translations[language]?.gtfsFeeds}</p>
                                    </div>
                                </div>
                                <div className='dash-gtfs-tile'>
                                    <span className='dash-gtfs-icon' style={{ background: '#884DFF' }}><FaBus size={14} color='white' /></span>
                                    <div>
                                        <p className='dash-gtfs-count'>{gtfsStats?.lines}</p>
                                        <p className='dash-gtfs-label'>{translations[language]?.routes}</p>
                                    </div>
                                </div>
                                <div className='dash-gtfs-tile'>
                                    <span className='dash-gtfs-icon' style={{ background: '#F59E0B' }}><FaMapMarkerAlt size={14} color='white' /></span>
                                    <div>
                                        <p className='dash-gtfs-count'>{gtfsStats?.stops}</p>
                                        <p className='dash-gtfs-label'>{translations[language]?.stops}</p>
                                    </div>
                                </div>
                                <div className='dash-gtfs-tile'>
                                    <span className='dash-gtfs-icon' style={{ background: '#1976D2' }}><FaLink size={14} color='white' /></span>
                                    <div>
                                        <p className='dash-gtfs-count'>{gtfsStats?.attachedVehicles}</p>
                                        <p className='dash-gtfs-label'>{translations[language]?.attachedVehicles}</p>
                                    </div>
                                </div>
                            </div>
                            {lineVehicleCounts?.length > 0 && (
                                <div className='dash-lines'>
                                    <div className='dash-lines-head'>
                                        <span className='dash-sub-heading'>{translations[language]?.vehiclesPerLine}</span>
                                        <span className='dash-lines-meta'>
                                            {lineVehicleCounts.length} {translations[language]?.routes}
                                        </span>
                                    </div>
                                    {(() => {
                                        const max = Math.max(...lineVehicleCounts.map((l) => l.count || 0), 1)
                                        return lineVehicleCounts.map((l) => (
                                            <div className='dash-line-row' key={l.line}>
                                                <span className='dash-line-badge'>{l.line}</span>
                                                <div className='dash-line-track'>
                                                    <span style={{ width: `${(l.count / max) * 100}%` }} />
                                                </div>
                                                <span className='dash-line-count'>{l.count}</span>
                                            </div>
                                        ))
                                    })()}
                                </div>
                            )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className='dashboard-big-box-flex'>
                    <div className='Vechicles-div'>
                        {titleRow(translations[language]?.vehicles, allPaths?.VEHICLES, vehiclesLoading ? null : vehicles?.length || 0)}
                        {vehiclesLoading ? (
                            <Skeleton active paragraph={{ rows: 5 }} />
                        ) : (
                            <Table
                                dataSource={vehicles?.slice(0, 6)}
                                columns={vehicleColumns}
                                pagination={false}
                                scroll={{ x: 300 }}
                                className='dashboard-table dashboard-table-clickable'
                                rowKey={(r) => r?.id || r?.vehicle_name}
                                onRow={() => ({ onClick: () => history?.push(allPaths?.VEHICLES) })}
                                locale={{
                                    emptyText: (
                                        <div className='dash-empty'>
                                            <span className='dash-empty-icon'><FaCarAlt size={24} /></span>
                                            <p className='dash-empty-title'>{translations[language]?.noVehiclesFound}</p>
                                        </div>
                                    ),
                                }}
                            />
                        )}
                    </div>
                    <div className='Vechicles-div'>
                        {titleRow(translations[language]?.devices, allPaths?.DEVICES, devicesLoading ? null : devices?.length || 0)}
                        {devicesLoading ? (
                            <Skeleton active paragraph={{ rows: 5 }} />
                        ) : (
                            <Table
                                dataSource={devices?.slice(0, 6)}
                                columns={deviceColumns}
                                pagination={false}
                                scroll={{ x: 300 }}
                                className='dashboard-table dashboard-table-clickable'
                                rowKey={(r) => r?.id || r?.serial_number}
                                onRow={() => ({ onClick: () => history?.push(allPaths?.DEVICES) })}
                                locale={{
                                    emptyText: (
                                        <div className='dash-empty'>
                                            <span className='dash-empty-icon'><FaCamera size={24} /></span>
                                            <p className='dash-empty-title'>{translations[language]?.noDevicesFound}</p>
                                        </div>
                                    ),
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Home
